import axiosInstance from './axiosInstance';

// ─── 게시글 ──────────────────────────────────────────────────────────────────

/**
 * GET /api/community/posts
 * @param {{ category?: string, search?: string, page?: number, size?: number }} params
 * @returns {{ content: Post[], totalElements: number, totalPages: number, page: number }}
 */
export const getPosts = async (params = {}) => {
  const res = await axiosInstance.get('/api/community/posts', { params });
  return res.data;
};

/**
 * GET /api/community/posts/:postId
 * @returns Post (with comments[] and isLiked)
 */
export const getPost = async (postId) => {
  const res = await axiosInstance.get(`/api/community/posts/${postId}`);
  return res.data;
};

/**
 * POST /api/community/posts
 * @param {{ category: 'FREE'|'QNA', title: string, content: string }} data
 */
export const createPost = async (data) => {
  const res = await axiosInstance.post('/api/community/posts', data);
  return res.data;
};

/**
 * PUT /api/community/posts/:postId
 * @param {{ title: string, content: string }} data
 */
export const updatePost = async (postId, data) => {
  const res = await axiosInstance.put(`/api/community/posts/${postId}`, data);
  return res.data;
};

/**
 * DELETE /api/community/posts/:postId
 */
export const deletePost = async (postId) => {
  await axiosInstance.delete(`/api/community/posts/${postId}`);
};

// ─── 댓글 ───────────────────────────────────────────────────────────────────

/**
 * POST /api/community/posts/:postId/comments
 * @param {{ content: string }} data
 * QnA 게시글에는 운영 계정만 가능 (403 응답 시 서버에서 차단)
 */
export const createComment = async (postId, data) => {
  const res = await axiosInstance.post(`/api/community/posts/${postId}/comments`, data);
  return res.data;
};

/**
 * DELETE /api/community/comments/:commentId
 */
export const deleteComment = async (commentId) => {
  await axiosInstance.delete(`/api/community/comments/${commentId}`);
};

// ─── 읽음 처리 ───────────────────────────────────────────────────────────────

/**
 * POST /api/community/posts/:postId/read — 로그인 필요
 */
export const markPostRead = async (postId) => {
  await axiosInstance.post(`/api/community/posts/${postId}/read`);
};

/**
 * GET /api/community/posts/read — 로그인 필요
 * @returns {{ readPostIds: number[] }}
 */
export const getReadPostIds = async () => {
  const res = await axiosInstance.get('/api/community/posts/read');
  return res.data;
};

// ─── 공감 (토글) ─────────────────────────────────────────────────────────────

/**
 * POST /api/community/posts/:postId/like
 * @returns {{ liked: boolean, likeCount: number }}
 */
export const togglePostLike = async (postId) => {
  const res = await axiosInstance.post(`/api/community/posts/${postId}/like`);
  return res.data;
};

/**
 * POST /api/community/comments/:commentId/like
 * @returns {{ liked: boolean, likeCount: number }}
 */
export const toggleCommentLike = async (commentId) => {
  const res = await axiosInstance.post(`/api/community/comments/${commentId}/like`);
  return res.data;
};

// ─── 응답 정규화 ──────────────────────────────────────────────────────────────
// 백엔드 응답을 프론트 컴포넌트가 사용하는 형태로 변환

const CATEGORY_MAP = {
  NOTICE: '공지사항',
  FREE: '자유게시판',
  QNA: 'QnA',
};

const CATEGORY_REVERSE_MAP = {
  '공지사항': 'NOTICE',
  '자유게시판': 'FREE',
  'QnA': 'QNA',
};

export const toApiCategory = (label) => CATEGORY_REVERSE_MAP[label] ?? label;

export const normalizePost = (post) => ({
  id: post.id,
  category: CATEGORY_MAP[post.category] ?? post.category,
  title: post.title,
  content: post.content ?? post.preview ?? '',
  author: post.authorName,
  authorName: post.authorName,
  authorId: post.authorId ?? post.memberId ?? post.writerId ?? null,
  authorProfileImage: post.authorProfileImage ?? post.authorProfileImageUrl ?? post.authorImageUrl ?? post.profileImageUrl ?? post.profileImage ?? null,
  isWriter: post.isWriter ?? post.isMine ?? post.isOwner ?? null,
  authorInitial: post.authorName?.[0] ?? '?',
  pinned: post.pinned ?? false,
  likes: post.likeCount ?? 0,
  isLiked: post.isLiked ?? false,
  views: post.viewCount ?? 0,
  date: post.createdAt?.slice(0, 10) ?? '',
  isNew: post.isNew ?? false,
  comments: (post.comments ?? []).map(normalizeComment),
  commentCount: post.commentCount ?? (post.comments?.length ?? 0),
  hasPoll: post.hasPoll ?? false,
  attachments: post.attachments ?? [],
  poll: post.poll ?? null,
});

// ─── 고정 (admin 전용) ────────────────────────────────────────────────────────

export const pinPost = async (postId) => {
  await axiosInstance.post(`/api/community/posts/${postId}/pin`);
};

export const unpinPost = async (postId) => {
  await axiosInstance.delete(`/api/community/posts/${postId}/pin`);
};

// ─── 투표 ─────────────────────────────────────────────────────────────────────

export const votePoll = async (pollId, optionIds) => {
  await axiosInstance.post(`/api/community/polls/${pollId}/vote`, { optionIds });
};

export const cancelVotePoll = async (pollId) => {
  await axiosInstance.delete(`/api/community/polls/${pollId}/vote`);
};

export const normalizeComment = (comment) => ({
  id: comment.id,
  author: comment.authorName,
  authorName: comment.authorName,
  authorId: comment.authorId ?? comment.memberId ?? comment.writerId ?? null,
  authorProfileImage: comment.authorProfileImage ?? comment.authorProfileImageUrl ?? comment.authorImageUrl ?? comment.profileImageUrl ?? comment.profileImage ?? null,
  isWriter: comment.isWriter ?? comment.isMine ?? comment.isOwner ?? null,
  authorInitial: comment.authorName?.[0] ?? '?',
  content: comment.content,
  date: comment.createdAt?.slice(0, 10) ?? '',
  likes: comment.likeCount ?? 0,
  isLiked: comment.isLiked ?? false,
});
